import type { DeveloperGroup } from '@company/shared'

/**
 * 项目成员类型（简化版，用于 flattenMembers 函数）
 */
export interface ProjectMemberBase {
  employeeId: string
  developerGroup: DeveloperGroup | null
  isLeader: boolean
}

/**
 * 分组的项目成员结构
 */
export interface GroupedMembers<T extends ProjectMemberBase = ProjectMemberBase> {
  owner: T | null
  function: T[]
  developers: Record<DeveloperGroup, T[]>
}

/**
 * 扁平化的成员信息
 */
export interface FlattenedMember {
  employeeId: string
  developerGroup: DeveloperGroup | null
  isLeader: boolean
}

/**
 * 从 GroupedMembers 提取扁平化的成员列表
 *
 * @param groupedMembers - 分组的项目成员
 * @returns 扁平化的成员数组
 */
export function flattenMembers<T extends ProjectMemberBase>(
  groupedMembers: GroupedMembers<T> | undefined
): FlattenedMember[] {
  if (!groupedMembers) {
    return []
  }

  const result: FlattenedMember[] = []

  // Add owner
  if (groupedMembers.owner) {
    result.push({
      employeeId: groupedMembers.owner.employeeId,
      developerGroup: groupedMembers.owner.developerGroup,
      isLeader: groupedMembers.owner.isLeader,
    })
  }

  // Add developers from all groups
  const developerGroups = Object.keys(groupedMembers.developers) as DeveloperGroup[]
  for (const group of developerGroups) {
    for (const member of groupedMembers.developers[group]) {
      result.push({
        employeeId: member.employeeId,
        developerGroup: member.developerGroup,
        isLeader: member.isLeader,
      })
    }
  }

  return result
}
